/** The Web Lock every tab of this origin takes around a refresh-token rotation. */
export const REFRESH_LOCK_NAME = 'awards-auth-refresh';
/** The channel a tab announces a successful rotation on. */
export const REFRESH_CHANNEL_NAME = 'awards-auth-refresh';
/**
 * A token another tab obtained this recently is adopted instead of rotating
 * again. A freshly issued access token lives for minutes (JWT_TTL_SECONDS), so
 * one minted in the last few seconds is still good.
 */
export const ADOPT_WINDOW_MS = 10_000;

/** A rotation's new access token, or a logout: drop any token cached for adoption. */
type RefreshMessage = { t: 'rotated'; token: string } | { t: 'cleared' };

export interface CrossTabRefreshDeps {
  /** POST /auth/refresh with the httpOnly cookie; the new access token, or null. */
  rotate: () => Promise<string | null>;
  /** Store the access token in this tab's memory. */
  setToken: (token: string) => void;
  /** `null` means absent (the fallback); omitted means the browser's. */
  locks?: LockManager | null;
  createChannel?: (name: string) => BroadcastChannel | null;
  now?: () => number;
}

/** Refresh, passing the access token whose request just failed (never re-adopted). */
export interface CrossTabRefresh {
  (failingToken?: string | null): Promise<boolean>;
  /** Logout: forget the adoptable token here and tell every other tab to do the same. */
  clear(): void;
}

/**
 * Serialise refresh-token rotation across every tab of this browser (H1).
 *
 * All tabs share ONE httpOnly refresh cookie, and the server treats a second
 * presentation of an already-rotated secret as token theft: it revokes the
 * whole session family and raises a security alert. The in-tab single-flight
 * in the API client cannot stop two TABS refreshing at once, which the bell's
 * relayed nudges make routine (every idle tab refetches together with an
 * expired access token).
 *
 * So a rotation runs inside the Web Lock `awards-auth-refresh`. The tab that
 * wins announces the new access token on a same-origin BroadcastChannel; a tab
 * that waited behind it adopts that token instead of rotating again. If it did
 * rotate anyway, it would at least present the cookie the first rotation just
 * set, never the stale one. The server's reuse detection is untouched.
 *
 * Adoption never takes the token the caller just saw fail (a revoked session
 * would otherwise re-adopt it for the whole window), and a logout posts
 * `cleared` so no tab adopts a token broadcast just before it.
 *
 * The lock holder is bounded by the caller's `rotate` (the API client gives the
 * refresh fetch a 15 s timeout), so the queue always drains. The WAIT for the
 * lock is deliberately not timed out: a waiter that gave up and rotated on its
 * own would present the cookie the holder is rotating, which is exactly the
 * reuse race the lock exists to prevent.
 *
 * Without Web Locks, or when the lock request itself is refused (SecurityError
 * in a sandboxed frame, AbortError), the rotation runs directly, as it always did.
 */
export function createCrossTabRefresh(deps: CrossTabRefreshDeps): CrossTabRefresh {
  const now = deps.now ?? Date.now;
  let resolvedLocks: LockManager | null | undefined = deps.locks;
  let channel: BroadcastChannel | null | undefined;
  let latest: { token: string; at: number } | null = null;

  // Opened lazily, on the first refresh, so importing the client opens nothing.
  const channelOnce = (): BroadcastChannel | null => {
    if (channel !== undefined) return channel;
    channel = (deps.createChannel ?? browserChannel)(REFRESH_CHANNEL_NAME);
    if (channel) {
      channel.onmessage = (e: MessageEvent<RefreshMessage>) => {
        if (e.data?.t === 'rotated' && typeof e.data.token === 'string') latest = { token: e.data.token, at: now() };
        else if (e.data?.t === 'cleared') latest = null;
      };
    }
    return channel;
  };

  const rotateAndAnnounce = async (): Promise<boolean> => {
    const token = await deps.rotate();
    if (!token) return false;
    deps.setToken(token);
    channelOnce()?.postMessage({ t: 'rotated', token } satisfies RefreshMessage);
    return true;
  };

  const refresh = async (failingToken?: string | null): Promise<boolean> => {
    if (resolvedLocks === undefined) resolvedLocks = browserLocks();
    const locks = resolvedLocks;
    if (!locks) return rotateAndAnnounce();
    channelOnce(); // listen before queueing so a rotation that lands while waiting is heard
    let granted = false;
    try {
      return await locks.request(REFRESH_LOCK_NAME, async () => {
        granted = true;
        const fresh = latest;
        if (fresh && fresh.token !== failingToken && now() - fresh.at <= ADOPT_WINDOW_MS) {
          deps.setToken(fresh.token);
          return true;
        }
        return rotateAndAnnounce();
      });
    } catch {
      // Refused before it ran (no lock was ever held): rotate directly, as
      // without Web Locks. A failure inside the lock is just a failed refresh;
      // retrying it outside the lock would reopen the race.
      return granted ? false : rotateAndAnnounce();
    }
  };

  return Object.assign(refresh, {
    clear(): void {
      latest = null;
      channelOnce()?.postMessage({ t: 'cleared' } satisfies RefreshMessage);
    },
  });
}

function browserLocks(): LockManager | null {
  return typeof navigator !== 'undefined' && 'locks' in navigator && navigator.locks ? navigator.locks : null;
}

function browserChannel(name: string): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  const ch = new BroadcastChannel(name);
  // Node's channel (tests) would otherwise keep the process alive.
  (ch as unknown as { unref?: () => void }).unref?.();
  return ch;
}
